<template>
  <div class="product-card-grid" data-test="product-card-grid">
    <UiSpinner v-if="loading" :size="30" class="align-self-center" />
    <UiText
      v-if="!products?.length && !loading"
      variant="body-3"
      color="grey-800"
      class="text-center mt-5"
    >
      <AppTranslate identifier="common.nothingFound.text" />
    </UiText>
    <div
      v-if="products?.length && !loading"
      class="product-card-grid__grid"
      data-test="product-card-grid-wrapper-card"
    >
      <ProductCard
        v-for="(product, index) in products"
        :key="product.id"
        v-model:favorites="favoritesModelValue"
        :product="product"
        :selected="selected"
        :has-bg="hasBgProductCard"
        @update:selected="handleUpdateSelected"
        @update:product="handleUpdateProduct($event, index)"
        @add-to-basket="$emit('addToBasket', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ProductCard from './ProductCard.vue';
import { type Product } from '~/domains/product';
import { UiSpinner, UiText } from '~/ui';
import { AppTranslate } from '~/infrastructure';

export interface Props {
  products: Product[] | null;
  favorites: string[];
  selected?: Product[];
  hasBgProductCard?: boolean;
  enableRefreshCells?: boolean;
  loading: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: 'update:favorites', value: string[]): void;
  (e: 'update:selected', value: Product[]): void;
  (
    e: 'addToBasket',
    value: { products: Product[]; hideLoadingCallback: () => void },
  ): void;
  (e: 'update:products', value: Product[]): void;
}>();

const favoritesModelValue = computed({
  get: () => props.favorites,
  set: (newValue) => {
    emit('update:favorites', newValue);
  },
});

const handleUpdateSelected = (value: Product[]): void => {
  emit('update:selected', value);
};

const handleUpdateProduct = (value: Product, index: number): void => {
  if (props.products !== null) {
    const newProductsData = props.products.map((product, idx) =>
      idx === index ? value : product,
    );

    emit('update:products', newProductsData);
  }
};
</script>

<style scoped lang="scss">
.product-card-grid {
  display: flex;
  flex-direction: column;

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(214px, 1fr));
    gap: 20px;
  }
}
</style>
